import * as React from 'react';
import { connect } from 'react-redux';

import { Link as LinkClass } from './Link';
import classnames from 'classnames';
import { __ } from '../helpers/TranslationService';
import { TrackComponent } from './TrackComponent';
import { IState } from '../reducers';

interface StateProps {
    baseUrl?: string;
    positionSlug: string;
    showLivePositionTab?: boolean;
    isReviewShown?: boolean;
    reviewRating: number;
    shipTitle: string;
    showSearchButton?: boolean;
    activeTab: string;
}

interface ComponentProps {
    offerUrl: string;
    sections: Section[];
}
type Props = StateProps & ComponentProps;

export type Section =
    | 'overview'
    | 'offers'
    | 'furtherInfo'
    | 'ships'
    | 'harbours'
    | 'reviews'
    | 'position'
    | 'cabins';
export class NavigationPanelClass extends React.Component<Props> {
    static defaultProps = {
        showLivePositionTab: false,
        isReviewShown: false,
        showSearchButton: false,
    };
    state = {
        activeTab: null,
    };

    constructor(props) {
        super(props);
        this.clickHandler = this.clickHandler.bind(this);
    }

    buildSection = (section: Section, icon: string, title: string) => {
        const liClasses = classnames('navigation-panel__item', {
            'navigation-panel__item--active': this.state.activeTab === section,
        });
        const aClasses = classnames('navigation-panel__link', {
            'navigation-panel__link--active': this.state.activeTab === section,
        });
        const Link = TrackComponent(LinkClass, {
            action: `navigation-${section}`,
            label: this.props.shipTitle,
        });

        return (
            <li className={liClasses} key={`navigation-${section}`}>
                <Link
                    href={`${this.props.baseUrl}#${section}`}
                    onClick={this.clickHandler(section)}
                    className={aClasses}
                >
                    <span className={icon}></span>
                    <span className="navigation-panel__item-title">{title}</span>
                </Link>
            </li>
        );
    };

    getReviewScore = () =>
        `${__('Reviews', 'dreamlines')} ${
            this.props.reviewRating?.toFixed ? this.props.reviewRating.toFixed(1) : '-'
        } / 10`;

    sections = {
        overview: this.buildSection('overview', 'icon-info', __('overview', 'dreamlines')),
        furtherInfo: this.buildSection(
            'furtherInfo',
            'icon-dreamlinesPackage-expert',
            __('Travel Tips', 'dreamlines'), // TODO add translation
        ),
        offers: this.buildSection('offers', 'icon-star', __('Our Recommendation', 'dreamlines')),
        ships: this.buildSection('ships', 'icon-shipv2', __('ships', 'dreamlines')),
        harbours: this.buildSection('harbours', 'icon-anchor', __('Harbours', 'dreamlines')),
        reviews: this.props.isReviewShown
            ? this.buildSection('reviews', 'icon-ratings', this.getReviewScore())
            : null,
        cabins: this.buildSection('cabins', 'icon-decks', __('Decks and Cabins', 'dreamlines')),
        position: this.props.showLivePositionTab
            ? this.buildSection('position', 'icon-marker', __('position', 'dreamlines'))
            : null,
    };
    clickHandler(id: Section) {
        return (props) => {
            this.setState({ activeTab: id });
            const index = props.href.indexOf('#');
            const targetElm = props.href.substr(index + 1);
            const scrollToEl = document.getElementById(targetElm);
            if (scrollToEl) {
                scrollToEl.scrollIntoView({ behavior: 'smooth' });
            }
        };
    }

    render() {
        if (!this.props.sections || this.props.sections.length === 0) return null;

        const Link = TrackComponent(LinkClass, { action: 'navigation-all-offers', label: 'navi' });

        return (
            <nav className="navigation-panel onlyDesktop">
                <ul className="navigation-panel__wrapper">
                    {this.props.sections.map((section: Section) => this.sections[section])}

                    {this.props.showSearchButton && (
                        <li className="navigation-panel__item navigation-panel__item--external">
                            <Link
                                href={this.props.offerUrl}
                                className="navigation-panel__link navigation-panel__link--external"
                                followLink
                            >
                                <span className="navigation-panel__item-title">
                                    {__('show all offers', 'dreamlines')}
                                </span>
                                &nbsp;
                                <span className="icon-arrowRight"></span>
                            </Link>
                        </li>
                    )}
                </ul>
            </nav>
        );
    }
}

const mapStateToProps = (state: IState) => {
    const { reviews } = state.reviews;
    return {
        positionSlug: state.shipData.shipPage?.shipPositionSlug,
        showLivePositionTab: state.shipData.shipPage?.showLivePosition,
        isReviewShown: reviews?.rating && reviews.rating.meta.total > 0,
        reviewRating: reviews?.aggregation ? reviews.aggregation.data.attributes.overall : 0,
        shipTitle: state.shipData.shipPage?.shipTitle,
        activeTab: state.ui.activeTab,
        path: state.router.path,
        baseUrl: state.router.baseUrl,
    };
};

export const NavigationPanel = connect(mapStateToProps)(NavigationPanelClass);

export default NavigationPanel;
